import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQuery,
  VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamWorkbooksConnection_nodes,
  VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQuery';
import {
  VirtualConnectionTableDownstreamWorkbooksQuery,
  VirtualConnectionTableDownstreamWorkbooksQuery_virtualConnectionTables_downstreamWorkbooksConnection_nodes,
  VirtualConnectionTableDownstreamWorkbooksQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamWorkbooksQuery';
import { WorkbookSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQueryGql';
import {
  VirtualConnectionTableDownstreamWorkbooksQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamWorkbooksQueryGql';
import {
  getRowDataForWorkbook,
  WorkbooksDisplayGrid,
  WorkbooksDisplayGridRowData,
} from '../sharedDisplayGrid/WorkbooksDisplayGrid';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

type QueryVariables = VirtualConnectionTableDownstreamWorkbooksQueryVariables;
type QueryResults = VirtualConnectionTableDownstreamWorkbooksQuery;
type QueryResultListNode = VirtualConnectionTableDownstreamWorkbooksQuery_virtualConnectionTables_downstreamWorkbooksConnection_nodes;
type GridRowData = WorkbooksDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamWorkbooksConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  return virtualConnection ? virtualConnection.downstreamWorkbooksConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.downstreamWorkbooksConnection : null;
}

export type VirtualConnectionTableDownstreamWorkbooksQueryGridProps =
  GridSortProps<WorkbooksValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionTableDownstreamWorkbooksQueryGrid(props: VirtualConnectionTableDownstreamWorkbooksQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamWorkbooksLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredWorkbooksLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamWorkbooksFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as WorkbookSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForWorkbook}
    >
      {({ rowStore }) => <WorkbooksDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamWorkbooksQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as WorkbookSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForWorkbook}
    >
      {({ rowStore }) => <WorkbooksDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
